import { Button, Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import React, { useEffect, useState, useMemo } from 'react';
import { setIsLoading } from '../general/actions';
import { currencyFormater, downloadExcel } from '../general/utils';
import { C } from '../general/colors';
import { Link } from 'react-router-dom';

const STATUS_STYLE = {
    1: { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
    2: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    3: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
};

function StatusBadge({ status }) {
    const s = STATUS_STYLE[status?.id] || { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
            borderRadius: 999, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        }}>
            {status?.description || '—'}
        </span>
    );
}

function Stepper({ value, onChange, min = 1, max = 90, label }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--abi-muted)', fontWeight: 600 }}>{label}</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--abi-border)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <button onClick={() => onChange(Math.max(min, value - 1))} style={{ background: 'none', border: 'none', padding: '4px 10px', cursor: 'pointer', color: 'var(--abi-primary)', fontWeight: 700, fontSize: 14 }}>−</button>
                <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 6px', borderLeft: '1px solid var(--abi-border)', borderRight: '1px solid var(--abi-border)', minWidth: 56, textAlign: 'center' }}>
                    {value} días
                </span>
                <button onClick={() => onChange(Math.min(max, value + 1))} style={{ background: 'none', border: 'none', padding: '4px 10px', cursor: 'pointer', color: 'var(--abi-primary)', fontWeight: 700, fontSize: 14 }}>+</button>
            </div>
        </div>
    );
}

// Parsea DD/MM/YYYY o YYYY-MM-DD → Date local
function parseDate(dateStr) {
    if (!dateStr) return null;
    if (dateStr.includes('/')) {
        const [d, m, y] = dateStr.split('/').map(Number);
        return new Date(y, m - 1, d);
    }
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
}

// Convierte a YYYY-MM-DD para ordenar y agrupar
function toSortKey(dateStr) {
    if (!dateStr) return '';
    if (dateStr.includes('/')) {
        const [d, m, y] = dateStr.split('/');
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return dateStr;
}

function formatDayLabel(dateStr) {
    const date = parseDate(dateStr);
    if (!date || isNaN(date)) return dateStr;
    const today    = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    if (date.getTime() === today.getTime())    return 'HOY';
    if (date.getTime() === tomorrow.getTime()) return 'MAÑANA';
    return date.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase();
}

function isToday(dateStr) {
    const date = parseDate(dateStr);
    if (!date || isNaN(date)) return false;
    const t = new Date();
    return date.getFullYear() === t.getFullYear() && date.getMonth() === t.getMonth() && date.getDate() === t.getDate();
}

function isTomorrow(dateStr) {
    const date = parseDate(dateStr);
    if (!date || isNaN(date)) return false;
    const t = new Date(); t.setDate(t.getDate() + 1);
    return date.getFullYear() === t.getFullYear() && date.getMonth() === t.getMonth() && date.getDate() === t.getDate();
}

function dayAccent(dateStr) {
    if (isToday(dateStr))    return { border: C.primary, bg: '#eff6ff', dot: C.primary };
    if (isTomorrow(dateStr)) return { border: C.accent,  bg: '#f5f3ff', dot: C.accent  };
    return { border: 'var(--abi-border)', bg: '#f8fafc', dot: '#94a3b8' };
}

/* ────────────────────────────────────────────────────────────────────────────── */
function DeliverySchedule({ setIsLoading, is_provider }) {
    const [orders,    setOrders]    = useState([]);
    const [statuses,  setStatuses]  = useState([]);
    const [days,      setDays]      = useState(parseInt(localStorage.deliveryDays) || 14);
    const [view,      setView]      = useState('grouped'); // 'grouped' | 'table'
    const [search,    setSearch]    = useState('');
    const [statFilter, setStatFilter] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        axiosInstance.get('/order-statuses').then(r => setStatuses(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => { localStorage.deliveryDays = days; fetchOrders(); }, [days]);

    const fetchOrders = async () => {
        setIsLoading(true);
        try {
            const r = await axiosInstance.get('/orders/next-delivery/' + days);
            setOrders(r.data || []);
            if (!(r.data || []).length) setMessage('Sin entregas programadas en este período.');
            else setMessage('');
        } catch (e) { console.log(e); setMessage('Error al cargar las entregas.'); }
        finally { setIsLoading(false); }
    };

    /* Nombre de la contraparte según rol */
    const counterpart = (o) => is_provider
        ? (o.agreement?.customer?.ext_id + ' ' + o.agreement?.customer?.name)
        : (o.agreement?.provider?.ext_id + ' ' + o.agreement?.provider?.name);

    /* Filtrado por búsqueda + estado */
    const filtered = useMemo(() => orders.filter(o => {
        const q = search.toLowerCase();
        const matchSearch = !q || [
            String(o.id), o.delivery_address, o.delivery_person_receiving,
            counterpart(o), o.agreement?.ext_id,
        ].some(v => (v || '').toLowerCase().includes(q));
        const matchStat = !statFilter || o.order_status?.id === parseInt(statFilter);
        return matchSearch && matchStat;
    }), [orders, search, statFilter]);

    /* Agrupación: fecha → contraparte+dirección → pedidos */
    const grouped = useMemo(() => {
        const byDate = {};
        filtered.forEach(o => {
            const date    = toSortKey(o.delivery_date);
            const party   = counterpart(o);
            const address = o.delivery_address || 'Sin dirección especificada';
            const key     = `${party}|||${address}`;
            if (!byDate[date]) byDate[date] = {};
            if (!byDate[date][key]) byDate[date][key] = { party, address, orders: [] };
            byDate[date][key].orders.push(o);
        });
        return Object.entries(byDate)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, groups]) => ({
                date,
                groups: Object.values(groups).sort((a, b) => a.party.localeCompare(b.party)),
                dayTotal:  Object.values(groups).reduce((s, g) => s + g.orders.reduce((ss, o) => ss + parseFloat(o.total_amount || 0), 0), 0),
                dayOrders: Object.values(groups).reduce((s, g) => s + g.orders.length, 0),
            }));
    }, [filtered]);

    /* KPIs globales */
    const stats = useMemo(() => ({
        total:     filtered.length,
        value:     filtered.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0),
        providers: new Set(filtered.map(o => counterpart(o))).size,
        addresses: new Set(filtered.map(o => o.delivery_address || '')).size,
        days:      new Set(filtered.map(o => o.delivery_date)).size,
    }), [filtered]);

    /* Excel (filas planas) */
    const handleExcel = () => {
        const headers = ['Fecha', 'Hora', is_provider ? 'Cliente' : 'Proveedor', 'Contrato', 'Pedido', 'Estado', 'Dirección', 'Recibe', 'Teléfono', 'Evento', 'Productos', 'Total'];
        const rows = filtered.map(o => [
            o.delivery_date, o.delivery_time, counterpart(o),
            o.agreement?.ext_id, o.id, o.order_status?.description,
            o.delivery_address, o.delivery_person_receiving, o.delivery_phone,
            o.delivery_event_name || '',
            (o.order_detail || []).length,
            parseFloat(o.total_amount || 0),
        ]);
        downloadExcel(headers, rows, `Entregas programadas ${days} días`, 'Entregas');
    };

    return (
        <>
            {/* ── Ayuda ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
                <i className="fa fa-lightbulb-o" style={{ color: 'var(--abi-primary)', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--abi-text)', lineHeight: 1.65 }}>
                    <strong>Entregas programadas:</strong> vista consolidada de pedidos agrupados por fecha,{' '}
                    {is_provider ? 'cliente' : 'proveedor'} y dirección de entrega.
                    Úsala para planificar la logística del período — ajusta los días, filtra por estado
                    o busca una dirección/proveedor específico. La vista <strong>Tabla</strong> permite exportar a Excel.
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div className="order-section" style={{ marginBottom: 14 }}>
                <div className="order-section-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <span><i className="fa fa-calendar-check-o" /> Entregas programadas</span>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                        <Stepper value={days} onChange={setDays} label="Período:" />
                        {/* Filtro estado */}
                        <select value={statFilter} onChange={e => setStatFilter(e.target.value)}
                            style={{ fontSize: 12, border: '1px solid var(--abi-border)', borderRadius: 8, padding: '4px 8px', background: '#fff' }}>
                            <option value="">Todos los estados</option>
                            {statuses.map(st => (
                                <option key={st.id} value={st.id}>{st.description}</option>
                            ))}
                        </select>
                        {/* Búsqueda */}
                        <div style={{ position: 'relative' }}>
                            <i className="fa fa-search" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--abi-muted)', fontSize: 11 }} />
                            <input
                                value={search} onChange={e => setSearch(e.target.value)}
                                placeholder="Buscar dirección, proveedor…"
                                style={{ fontSize: 12, border: '1px solid var(--abi-border)', borderRadius: 8, padding: '4px 8px 4px 26px', background: '#fff', width: 200 }}
                            />
                            {search && (
                                <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--abi-muted)', fontSize: 11 }}>✕</button>
                            )}
                        </div>
                        {/* Toggle vista */}
                        <div className="products-view-toggle">
                            <button className={`products-view-btn${view === 'grouped' ? ' active' : ''}`} onClick={() => setView('grouped')} title="Vista agrupada">
                                <i className="fa fa-th-large" />
                            </button>
                            <button className={`products-view-btn${view === 'table' ? ' active' : ''}`} onClick={() => setView('table')} title="Vista tabla">
                                <i className="fa fa-list" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {message && !filtered.length && <Alert variant="info" className="mb-3">{message}</Alert>}

            {filtered.length > 0 && (
                <>
                    {/* ── KPI chips ── */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                        {[
                            { icon: 'fa-shopping-basket', label: 'Pedidos',      value: stats.total,                        color: 'var(--abi-primary)' },
                            { icon: 'fa-money',           label: 'Valor total',  value: currencyFormater(stats.value),       color: C.accent    },
                            { icon: 'fa-truck',           label: is_provider ? 'Clientes' : 'Proveedores', value: stats.providers, color: C.provider },
                            { icon: 'fa-map-marker',      label: 'Direcciones',  value: stats.addresses,                    color: C.warning   },
                            { icon: 'fa-calendar-o',      label: 'Días con entregas', value: stats.days,                    color: '#64748b'           },
                        ].map(c => (
                            <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#fff', border: '1px solid var(--abi-border)', borderRadius: 10, padding: '6px 12px', fontSize: 13 }}>
                                <i className={`fa ${c.icon}`} style={{ color: c.color, fontSize: 13 }} />
                                <span style={{ color: 'var(--abi-muted)', fontWeight: 600 }}>{c.label}:</span>
                                <span style={{ fontWeight: 800, color: 'var(--abi-text)' }}>{c.value}</span>
                            </div>
                        ))}
                        <div style={{ marginLeft: 'auto' }}>
                            <Button variant="outline-success" size="sm" onClick={handleExcel}>
                                <i className="fa fa-file-excel-o" /> Excel
                            </Button>
                        </div>
                    </div>

                    {/* ══ Vista agrupada ══ */}
                    {view === 'grouped' && grouped.map(({ date, groups, dayTotal, dayOrders }) => {
                        const accent = dayAccent(date);
                        return (
                            <div key={date} style={{ marginBottom: 20 }}>
                                {/* Cabecera del día */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: accent.bg, border: `1px solid ${accent.border}`,
                                    borderRadius: '12px 12px 0 0', padding: '10px 16px',
                                    borderBottom: 'none',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: accent.dot, flexShrink: 0 }} />
                                        <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--abi-text)' }}>
                                            {formatDayLabel(date)}
                                        </span>
                                        <span style={{ fontSize: 12, color: 'var(--abi-muted)' }}>{date}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                                        <span style={{ color: 'var(--abi-muted)' }}>
                                            <strong style={{ color: 'var(--abi-text)' }}>{dayOrders}</strong> pedido{dayOrders !== 1 ? 's' : ''}
                                        </span>
                                        <span style={{ fontWeight: 700, color: 'var(--abi-primary)' }}>
                                            {currencyFormater(dayTotal)}
                                        </span>
                                    </div>
                                </div>

                                {/* Grupos por contraparte + dirección */}
                                <div style={{ border: `1px solid ${accent.border}`, borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                                    {groups.map((g, gi) => (
                                        <div key={gi} style={{ borderTop: gi === 0 ? 'none' : '1px solid var(--abi-border)' }}>
                                            {/* Header de proveedor/cliente */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#fff', borderBottom: '1px solid #f1f5f9' }}>
                                                <i className="fa fa-truck" style={{ color: 'var(--abi-primary)', fontSize: 12 }} />
                                                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--abi-text)' }}>{g.party}</span>
                                                <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--abi-muted)' }}>
                                                    <i className="fa fa-map-marker" style={{ marginRight: 3 }} />{g.address}
                                                </span>
                                                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--abi-muted)' }}>
                                                    {g.orders.length} pedido{g.orders.length !== 1 ? 's' : ''} ·{' '}
                                                    <strong>{currencyFormater(g.orders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0))}</strong>
                                                </span>
                                            </div>

                                            {/* Pedidos del grupo */}
                                            {g.orders
                                                .sort((a, b) => (a.delivery_time || '').localeCompare(b.delivery_time || ''))
                                                .map((o, oi) => (
                                                    <div key={o.id} style={{
                                                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                                                        padding: '10px 16px 10px 36px',
                                                        background: oi % 2 === 0 ? '#fafbff' : '#fff',
                                                        borderBottom: oi < g.orders.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                    }}>
                                                        {/* Hora */}
                                                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--abi-primary)', minWidth: 48, flexShrink: 0 }}>
                                                            {o.delivery_time || '—:—'}
                                                        </span>
                                                        {/* ID pedido */}
                                                        <Link to={'/order/search/' + o.id} style={{ fontWeight: 800, fontSize: 13, color: 'var(--abi-primary)', textDecoration: 'none', minWidth: 52 }}>
                                                            #{o.id}
                                                        </Link>
                                                        {/* Estado */}
                                                        <StatusBadge status={o.order_status} />
                                                        {/* Recibe */}
                                                        {o.delivery_person_receiving && (
                                                            <span style={{ fontSize: 12, color: 'var(--abi-muted)', flexShrink: 0 }}>
                                                                <i className="fa fa-user-o" style={{ marginRight: 4 }} />
                                                                {o.delivery_person_receiving}
                                                                {o.delivery_phone && <span style={{ marginLeft: 6 }}>{o.delivery_phone}</span>}
                                                            </span>
                                                        )}
                                                        {/* Evento */}
                                                        {o.delivery_event_name && (
                                                            <span style={{ fontSize: 12, color: 'var(--abi-muted)', flexShrink: 0 }}>
                                                                <i className="fa fa-tag" style={{ marginRight: 4 }} />{o.delivery_event_name}
                                                            </span>
                                                        )}
                                                        {/* Productos */}
                                                        <span style={{ fontSize: 12, color: 'var(--abi-muted)', flexShrink: 0 }}>
                                                            <i className="fa fa-cube" style={{ marginRight: 4 }} />
                                                            {(o.order_detail || []).length} ítem{(o.order_detail || []).length !== 1 ? 's' : ''}
                                                        </span>
                                                        {/* Total */}
                                                        <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 14 }}>
                                                            {currencyFormater(o.total_amount)}
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}

                    {/* ══ Vista tabla ══ */}
                    {view === 'table' && (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="report-table mb-0" style={{ minWidth: 720 }}>
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Hora</th>
                                        <th>Pedido</th>
                                        <th>Estado</th>
                                        <th>{is_provider ? 'Cliente' : 'Proveedor'}</th>
                                        <th className="d-none d-md-table-cell">Dirección</th>
                                        <th className="d-none d-lg-table-cell">Recibe</th>
                                        <th className="d-none d-sm-table-cell">Ítems</th>
                                        <th className="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered
                                        .sort((a, b) => toSortKey(a.delivery_date).localeCompare(toSortKey(b.delivery_date)) || (a.delivery_time || '').localeCompare(b.delivery_time || ''))
                                        .map(o => (
                                            <tr key={'t-' + o.id} className="summary-row">
                                                <td style={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: 13 }}>
                                                    {o.delivery_date}
                                                    {(isToday(o.delivery_date) || isTomorrow(o.delivery_date)) && (
                                                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: isToday(o.delivery_date) ? C.primary : C.accent }}>
                                                            {isToday(o.delivery_date) ? 'HOY' : 'MAÑANA'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ fontSize: 12, color: 'var(--abi-muted)', whiteSpace: 'nowrap' }}>{o.delivery_time}</td>
                                                <td>
                                                    <Link to={'/order/search/' + o.id} style={{ fontWeight: 800, color: 'var(--abi-primary)', textDecoration: 'none' }}>
                                                        #{o.id}
                                                    </Link>
                                                </td>
                                                <td><StatusBadge status={o.order_status} /></td>
                                                <td style={{ fontSize: 12 }}>{counterpart(o)}</td>
                                                <td className="d-none d-md-table-cell" style={{ fontSize: 12, color: 'var(--abi-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {o.delivery_address || '—'}
                                                </td>
                                                <td className="d-none d-lg-table-cell" style={{ fontSize: 12 }}>
                                                    {o.delivery_person_receiving}
                                                    {o.delivery_phone && <div style={{ color: 'var(--abi-muted)' }}>{o.delivery_phone}</div>}
                                                </td>
                                                <td className="d-none d-sm-table-cell text-center" style={{ fontWeight: 600, fontSize: 13 }}>
                                                    {(o.order_detail || []).length}
                                                </td>
                                                <td className="text-end" style={{ fontWeight: 700 }}>{currencyFormater(o.total_amount)}</td>
                                            </tr>
                                        ))}
                                </tbody>
                                <tfoot>
                                    <tr className="report-tfoot">
                                        <th colSpan={8} className="text-end">Total período</th>
                                        <th className="text-end">{currencyFormater(stats.value)}</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

function mapStateToProps(state) { return { isLoading: state.isLoading, is_provider: state.is_provider }; }
const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(DeliverySchedule);
